import {
    Aggregate,
    AggregateColumnDirective,
    AggregateColumnsDirective,
    AggregateDirective,
    AggregatesDirective,
    ColumnDirective,
    ColumnsDirective,
    Filter,
    FilterSettingsModel,
    GridComponent,
    Inject,
    Sort,
    SortComparer,
} from '@syncfusion/ej2-react-grids';
import { format } from 'date-fns';
import { FC, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useGetJournalEntries } from '../../api/amount/amount';
import { GridStateContextProvider } from '../../contexts/gridStateContext';
import { useEnterAmountsDialogStyles } from '../../hooks/useEnterAmountsDialogStyles';
import { useJournalEntryDialogStyles } from '../../hooks/useJournalEntryDialogStyles';
import { GetJournalEntriesResponse, IJournalEntry } from '../../model';
import { dateSortComparer, parseDate, US_SHORT_DATETIME_FORMAT } from '../../utils/DateUtils';
import LoadingSpinner from '../common/LoadingSpinner';
import { IJournalEntryGridProps } from './IJournalEntryGridProps';

export const JournalEntryGrid: FC<IJournalEntryGridProps> = (props: IJournalEntryGridProps) => {
    const numberFormat: string = '#,###;(#,###);0';
    const { t } = useTranslation();
    const { gridComponentCSSClass, totalRowStyle } = useJournalEntryDialogStyles();
    const { spinnerContainerClassName } = useEnterAmountsDialogStyles();
    const { data: response, isFetching } = useGetJournalEntries<GetJournalEntriesResponse>({
        AccountCode: props.accountCode,
        AdjustmentCode: props.adjustmentCode,
        CaseCode: props.caseCode,
        Jurisdiction: props.jurisdiction,
        EntityCode: props.entityCode,
        Period: props.period,
    });

    const dataSource: IJournalEntry[] | undefined = response?.journalEntries
        ? response.journalEntries.map((entry: IJournalEntry) => {
              const journalEntryWithDate: IJournalEntry = {
                  ...entry,
                  postedDate: entry.postedDate ? format(parseDate(entry.postedDate) as Date, US_SHORT_DATETIME_FORMAT) : undefined, // making sure formatting is consistent
              };
              return journalEntryWithDate;
          })
        : undefined;

    const filterOptions: FilterSettingsModel = {
        type: 'CheckBox',
        enableInfiniteScrolling: true,
    };
    const totalRowTemplate = useCallback(() => {
        return <span style={totalRowStyle}>{t('total').toString()}</span>;
    }, [t, totalRowStyle]);

    return (
        <>
            {!isFetching && dataSource && (
                <GridStateContextProvider grid={props.gridRef}>
                    <GridComponent
                        dataSource={dataSource}
                        ref={props.gridRef}
                        className={gridComponentCSSClass}
                        rowHeight={28}
                        filterSettings={filterOptions}
                        allowSorting={true}
                        allowFiltering={true}
                        actionComplete={props.gridActionComplete}
                        height={'100%'}
                    >
                        <ColumnsDirective>
                            <ColumnDirective field='accountCode' headerText='Account code' type='string' width={136} />
                            <ColumnDirective field='adjustmentCode' headerText='Adjustment code' type='string' width={155} />
                            <ColumnDirective
                                field='changeInBalance'
                                headerText='Change in balance'
                                type='number'
                                headerTextAlign='Left'
                                textAlign='Right'
                                format={numberFormat}
                                width={163}
                                allowFiltering={false}
                            />
                            <ColumnDirective field='accountType' headerText='Account type' type='string' width={136} />
                            <ColumnDirective field='description' headerText='Journal entry description' type='string' width={205} />
                            <ColumnDirective
                                field='postedDate'
                                headerText='Posted date'
                                type='string'
                                sortComparer={dateSortComparer as SortComparer} // syncfusion has a bug while handling dates. We keep them as strings, and use a custom comparer as a workaround
                                width={150}
                            />
                            <ColumnDirective field='postedBy' headerText='Posted by' type='string' width={150} />
                            <ColumnDirective field='source' headerText='Source' type='string' width={120} />
                        </ColumnsDirective>
                        <AggregatesDirective>
                            <AggregateDirective>
                                <AggregateColumnsDirective>
                                    <AggregateColumnDirective footerTemplate={totalRowTemplate} type='Min' field='accountCode' />
                                    <AggregateColumnDirective type='Sum' field='changeInBalance' format={numberFormat} />
                                </AggregateColumnsDirective>
                            </AggregateDirective>
                        </AggregatesDirective>
                        <Inject services={[Filter, Sort, Aggregate]} />
                    </GridComponent>
                </GridStateContextProvider>
            )}
            {isFetching && (
                <div className={spinnerContainerClassName}>
                    <LoadingSpinner id='loadingSpinnerJournalEntryGrid' label={t('loadingPleaseWait').toString()} />
                </div>
            )}
        </>
    );
};
